/** Build live context + rule facts for hybrid LLM responses. */

import { getPageHelp } from "assistant/knowledge";
import { buildPageAwareContext, formatPageAwareForLlm } from "assistant/pageAware";
import {
  fetchTeacherStats,
  fetchLeaveStats,
  fetchRecruiterCount,
  fetchAssignmentStats,
  fetchEstablishmentStats,
  fetchDashboardSummaryCards,
  fetchAreaAttendanceSummary,
  fetchEstablishmentAttendanceReport,
  searchTeachers,
} from "assistant/apiHandlers";
import { extractSearchTerm, extractTeacherStoryTerm } from "assistant/intents";
import { fetchTeacherStory } from "assistant/teacherStory";

function normalize(text) {
  return String(text ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

export function formatFactsForLlm(facts, language) {
  const lines = [];
  const fr = language !== "en";

  if (facts.teachers?.totalTeachers != null) {
    lines.push(
      fr
        ? `Enseignantes enregistrées : ${facts.teachers.totalTeachers}`
        : `Teachers registered: ${facts.teachers.totalTeachers}`
    );
  }
  if (facts.leaves) {
    const s = facts.leaves;
    lines.push(
      fr
        ? `Congés — total: ${s.total}, en attente: ${s.pending}, approuvés: ${s.approved}, rejetés: ${s.rejected}`
        : `Leave — total: ${s.total}, pending: ${s.pending}, approved: ${s.approved}, rejected: ${s.rejected}`
    );
  }
  if (facts.recruiters?.count != null) {
    lines.push(fr ? `Recruteurs : ${facts.recruiters.count}` : `Recruiters: ${facts.recruiters.count}`);
  }
  if (facts.assignments?.totalRecords != null) {
    lines.push(
      fr
        ? `Affectations — total: ${facts.assignments.totalRecords}, ce mois: ${facts.assignments.thisMonthTotal ?? 0}`
        : `Assignments — total: ${facts.assignments.totalRecords}, this month: ${facts.assignments.thisMonthTotal ?? 0}`
    );
  }
  if (facts.establishments?.totalRecords != null) {
    lines.push(
      fr
        ? `Établissements : ${facts.establishments.totalRecords}`
        : `Establishments: ${facts.establishments.totalRecords}`
    );
  }
  if (facts.dashboard?.length) {
    const snap = facts.dashboard.map((c) => `${c.label}: ${c.value}`).join(", ");
    lines.push(fr ? `Aperçu portail — ${snap}` : `Portal snapshot — ${snap}`);
  }
  if (facts.areaAttendance) {
    const a = facts.areaAttendance;
    lines.push(
      fr
        ? `Synthèse zone (${a.startDate}→${a.endDate}) : ${a.teachersChecked}/${a.teachersAssigned} vérifiées (${a.checkRate}%)`
        : `Area summary (${a.startDate}→${a.endDate}): ${a.teachersChecked}/${a.teachersAssigned} checked (${a.checkRate}%)`
    );
  }
  if (facts.establishmentAttendance) {
    const e = facts.establishmentAttendance;
    lines.push(
      fr
        ? `Rapport établissement (${e.startDate}→${e.endDate}) : ${e.withPresence}/${e.totalTeachers} avec présence`
        : `Establishment report (${e.startDate}→${e.endDate}): ${e.withPresence}/${e.totalTeachers} with presence`
    );
  }
  if (facts.licenses) {
    const l = facts.licenses;
    lines.push(
      fr
        ? `Licences : ${l.licensedCount}/${l.maxLicensedTeachers || "—"} licenciés, ${l.remainingLicenses} restants, ${l.unlicensedCount} non licenciés`
        : `Licenses: ${l.licensedCount}/${l.maxLicensedTeachers || "—"} licensed, ${l.remainingLicenses} remaining, ${l.unlicensedCount} unlicensed`
    );
  }
  if (facts.teacherStory) {
    const ts = facts.teacherStory;
    const t = ts.teacher || {};
    lines.push(
      fr
        ? `Fiche enseignant : ${t.name || "—"} (${t.reg || "—"}) · score ${t.score ?? "—"} · licenciée ${t.isLicensed ? "oui" : "non"} · affectation ${ts.assignment || "aucune"} · congés ${ts.leaves ?? 0}`
        : `Teacher story: ${t.name || "—"} (${t.reg || "—"}) · score ${t.score ?? "—"} · licensed ${t.isLicensed ? "yes" : "no"} · assignment ${ts.assignment || "none"} · leave ${ts.leaves ?? 0}`
    );
  }
  if (facts.search?.items?.length) {
    const names = facts.search.items.map((t) => `${t.name} (${t.reg})`).join(", ");
    lines.push(fr ? `Recherche enseignantes : ${names}` : `Teacher search: ${names}`);
  }
  if (facts.teacherStory?.structuredText) {
    lines.push(facts.teacherStory.structuredText);
  }

  return lines.join("\n");
}

/** Keyword-based live data prefetch for LLM context (no PII). */
export async function prefetchLiveContext(message, context, { broad = false } = {}) {
  const q = normalize(message);
  const facts = {};
  const jobs = [];

  const want = (re) => broad || re.test(q);

  if (want(/enseignant|teacher|prof|matricule/)) {
    jobs.push(fetchTeacherStats().then((r) => { facts.teachers = r; }));
  }
  if (want(/conge|congé|leave|absence|pending/)) {
    jobs.push(fetchLeaveStats().then((r) => { facts.leaves = r.stats; }));
  }
  if (want(/recruteur|recruiter/)) {
    jobs.push(fetchRecruiterCount().then((r) => { facts.recruiters = r; }));
  }
  if (want(/affectation|assignment/)) {
    jobs.push(fetchAssignmentStats().then((r) => { facts.assignments = r; }));
  }
  if (want(/etablissement|établissement|establishment|ecole|école/)) {
    jobs.push(fetchEstablishmentStats().then((r) => { facts.establishments = r; }));
  }
  if (want(/resume|résumé|summary|kpi|dashboard|portail|portal|situation/)) {
    jobs.push(
      fetchDashboardSummaryCards(context.language).then((r) => {
        facts.dashboard = r;
      })
    );
  }

  if (want(/presence|présence|attendance|zone|synthese|synthèse|rapport/)) {
    jobs.push(
      fetchAreaAttendanceSummary().then((r) => {
        facts.areaAttendance = r;
      })
    );
  }
  if (want(/etablissement|établissement|establishment|rapport presence|rapport présence/)) {
    jobs.push(
      fetchEstablishmentAttendanceReport().then((r) => {
        facts.establishmentAttendance = r;
      })
    );
  }
  if (want(/chercher|search|trouver|find|named|nomme|nommé|enseignant|teacher/)) {
    const term = extractSearchTerm(message, context.language);
    if (term && term.length >= 2) {
      jobs.push(
        searchTeachers(term, 5).then((r) => {
          facts.search = {
            term,
            count: r.totalMatches,
            items: (r.items || []).map(stripHeavyTeacherFields),
          };
        })
      );
    }
  }

  if (want(/story|profil|profile|fiche|assigned|affect|cong|leave|attend|present|histoire/)) {
    const term = extractTeacherStoryTerm(message, context.language);
    const presetId = context.pageContext?.selection?.teacherId;
    if ((term && term.length >= 2) || presetId) {
      jobs.push(
        fetchTeacherStory(term, {
          teacherId: presetId,
          pageContext: context.pageContext,
          language: context.language,
        }).then((r) => {
          if (r.ok) {
            facts.teacherStory = {
              ok: true,
              structuredText: r.structuredText,
              teacher: stripHeavyTeacherFields(r.story?.teacher),
              summary: r.story?.summary,
              ranking: r.story?.ranking,
              assignment: r.story?.currentAssignment?.estabName || null,
              leaves: r.story?.leaves?.length ?? 0,
            };
          }
        })
      );
    }
  }

  if (broad && jobs.length === 0) {
    jobs.push(
      fetchLeaveStats().then((r) => { facts.leaves = r.stats; }),
      fetchTeacherStats().then((r) => { facts.teachers = r; })
    );
  }

  await Promise.allSettled(jobs);

  const pageHelp = getPageHelp(context.pathname, context.language);
  const pageAware = buildPageAwareContext(context.pathname, context.pageContext, context.language);
  const pageAwareText = formatPageAwareForLlm(pageAware, context.language);
  const liveData = [formatFactsForLlm(facts, context.language), pageAwareText].filter(Boolean).join("\n");

  return {
    facts,
    liveData,
    pageHelp: pageHelp
      ? {
          title: pageHelp.title,
          summary: pageHelp.summary,
          steps: pageHelp.steps,
        }
      : null,
  };
}

export function stripHeavyTeacherFields(teacher) {
  if (!teacher || typeof teacher !== "object") return teacher;
  const { profilePic, ProfilePic, hasProfilePic, HasProfilePic, ...rest } = teacher;
  return {
    ...rest,
    hasPhoto: Boolean(profilePic || ProfilePic || hasProfilePic || HasProfilePic),
  };
}

export function sanitizeFactsForLlm(facts) {
  if (!facts || typeof facts !== "object") return facts;
  const next = { ...facts };
  if (Array.isArray(next.search?.items)) {
    next.search = {
      ...next.search,
      items: next.search.items.map(stripHeavyTeacherFields),
    };
  }
  if (next.teacherStory?.teacher) {
    next.teacherStory = {
      ...next.teacherStory,
      teacher: stripHeavyTeacherFields(next.teacherStory.teacher),
    };
  }
  if (next.teacherStory?.story?.teacher) {
    const story = { ...next.teacherStory.story };
    story.teacher = stripHeavyTeacherFields(story.teacher);
    delete story.detail;
    delete story.charts;
    delete story.recentAttendance;
    next.teacherStory = { ...next.teacherStory, story };
  }
  return next;
}

export function buildRuleFacts({ intent, structuredText, facts, pageHelp, faq, pageAware }) {
  return {
    intent: intent || "general",
    structuredText: structuredText || "",
    facts: sanitizeFactsForLlm(facts) || null,
    pageHelp: pageHelp || null,
    faq: faq ? { question: faq.question, answer: faq.answer } : null,
    pageAware: pageAware || null,
  };
}

export const LLM_ENHANCE_INTENTS = new Set([
  "help_page",
  "faq",
  "count_teachers",
  "count_recruiters",
  "count_leaves",
  "count_assignments",
  "count_establishments",
  "dashboard_summary",
  // teacher_story / search_teacher already ship rich cards — skip slow AI rewrite by default
  "area_attendance",
  "establishment_attendance",
  "license_summary",
  "insights",
  "hybrid",
  "open_chat",
]);

export const RULES_ONLY_INTENTS = new Set([
  "navigate",
  "clear",
  "greeting",
  "thanks",
  "wizard_affectation",
  "wizard_leave",
  "wizard_teacher",
  "wizard_attendance",
  "write_command",
  "command_confirm",
  "command_result",
]);
